import React, { useContext, useEffect, useState } from "react";
import styles from "../../form.module.css";
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  Row,
  Select,
  Typography,
  Image,
} from "antd";
import useFetch from "../../../../hooks/useFetch";
import useToast from "../../../../hooks/useToast";
import {
  eDocType,
  eFORMSTATUS,
  eFullTimeOrPartTime,
  eResultCode,
  INPUT_LENGTH,
  eAnswerType,
  eSTAGE,
  eUSER_FORM_TYPE,
} from "../../../../utils/enum";
import { ToastOpen, ToastType } from "../../../../state/toast/slice";
import {
  CheckCircleOutlined,
  CloseOutlined,
  ExclamationCircleOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";
import Text from "../../../../context/language-context";
import { Table } from "antd";
import type { TableProps } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import { UserContext } from "../../../../context";
import {
  GetFormData,
  updateField,
  UploadDocs,
} from "../../../../utils/api.constant";

import AuthUtil from "../../../../utils/auth";
import { FaCheck } from "react-icons/fa";
import { TFormProps } from "../../../../types";
import { LoaderContext } from "../../../../context/loader";
import type {
  SCHOOL_PT2_Q2 as SCHOOL_2026_PT2_Q2,
  SCHOOL_PT2_Q3 as SCHOOL_2026_PT2_Q3,
  SCHOOL_PT2_Q4 as SCHOOL_2026_PT2_Q4,
  SCHOOL_PT2_Q5 as SCHOOL_2026_PT2_Q5,
  SCHOOL_PT2_Q6 as SCHOOL_2026_PT2_Q6,
  SCHOOL_PT2_Q7 as SCHOOL_2026_PT2_Q7,
  SCHOOL_PT2_Q8 as SCHOOL_2026_PT2_Q8,
  SCHOOL_PT2_Q9 as SCHOOL_2026_PT2_Q9,
  DATA_TYPE_SCHOOL_PT2_Q10 as DATA_TYPE_SCHOOL_2026_PT2_Q10,
} from "../../../../types/table_types";
import {
  disableFutureYears,
  getNumberInLanguage,
} from "../../../../utils/helperFunctions";
import { Rule } from "antd/es/form";
import EncryptUtils from "../../../../utils/encrypt";
import dayjs from "dayjs";
import Utils from "../../../../utils";
import {
  disableFutureDates_on_input_date_max,
  disableFutureDates_on_input_month_max,
  numberInputRules,
  validateFile,
} from "../../../../utils/input.validator";
import {
  DocumentUploadField,
  DocumentUploadTableField,
} from "../../../../components/document-upload";
import { useFormAutoSave } from "../../../../hooks/useFormAutoSave";
import { useVedOptions } from "../../../../hooks/useVedOptions";
import FormHelpTooltip from "../../../../components/common/FormHelpTooltip";
import { CURRENT_YEAR } from "../../../../utils/constants";

// Define all Date field keys for this form to enable robust date conversion
const DATE_FIELD_KEYS = [
  "SCHOOL_2026_PT2_Q16", // Affidavit Date

  // Add other date fields if any
];

const formItemLayout = {
  labelCol: {
    span: 24,
  },
  wrapperCol: {
    span: 24,
  },
};

const SchoolPartTwo = ({
  disabled,
  currentFormId,
  currentUserId,
  currentFormFor,
}: TFormProps) => {
  const [form] = Form.useForm();
  const { post } = useFetch();
  const { onShowToast } = useToast();
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const [formId, setFormId] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | number | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [formFor, setFormFor] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState<Boolean>(false);

  // Loader Context For show Loader
  const contextForLoader = useContext(LoaderContext);
  const { isLoading, setIsLoading } = contextForLoader as any;

  const vedMapping = useVedOptions();

  // Watch affidavitAccepted to trigger re-renders when checkbox changes
  const affidavitAccepted = Form.useWatch("affidavitAccepted", form);

  const [applyPdfStyles, setApplyPdfStyles] = useState(false);
  const DROPDOWN_PLACEHOLDER = Text({
    tid: "DROPDOWN_PLACEHOLDER",
    def: "DROPDOWN_PLACEHOLDER",
  });

  // Placeholder texts for form fields
  const Q1_PLACEHOLDER = Text({
    tid: "SCHOOL_2026_PT2_Q1_PLACEHOLDER",
    def: "उदा॰ : सनातन वैदिक शिक्षण संस्थान",
  });

  const Q2_A_PLACEHOLDER = Text({
    tid: "SCHOOL_2026_PT2_Q2_A_PLACEHOLDER",
    def: "उदा॰ : उमेश द्विवेदी",
  });

  const Q2_D_PLACEHOLDER = Text({
    tid: "SCHOOL_2026_PT2_Q2_D_PLACEHOLDER",
    def: "उदा॰ : 35",
  });

  const Q3_A_PLACEHOLDER = Text({
    tid: "SCHOOL_2026_PT2_Q3_A_PLACEHOLDER",
    def: "उदा॰ : नारायण शर्मा",
  });

  const Q3_E_PLACEHOLDER = Text({
    tid: "SCHOOL_2026_PT2_Q3_E_PLACEHOLDER",
    def: "उदा॰ : लक्षणग्रन्थों के अध्ययनार्थ",
  });

  const Q4_A_PLACEHOLDER = Text({
    tid: "SCHOOL_2026_PT2_Q4_A_PLACEHOLDER",
    def: "उदा॰ : अनन्तकृष्ण भट्ट",
  });

  const Q4_B_PLACEHOLDER = Text({
    tid: "SCHOOL_2026_PT2_Q4_B_PLACEHOLDER",
    def: "उदा॰ : भारतात्मा आदर्श वेदाध्यापक पुरस्कार",
  });

  const Q4_C_PLACEHOLDER = Text({
    tid: "SCHOOL_2026_PT2_Q4_C_PLACEHOLDER",
    def: "उदा॰ : सिंघल फाउण्डेशन, उदयपुर",
  });

  const Q5_A_PLACEHOLDER = Text({
    tid: "SCHOOL_2026_PT2_Q5_A_PLACEHOLDER",
    def: "उदा॰ : श्रुतिधर शर्मा",
  });

  const Q5_B_PLACEHOLDER = Text({
    tid: "SCHOOL_2026_PT2_Q5_B_PLACEHOLDER",
    def: "उदा॰ : 16",
  });

  const Q6_A_PLACEHOLDER = Text({
    tid: "SCHOOL_2026_PT2_Q6_A_PLACEHOLDER",
    def: "उदा॰ : श्रुतिधर शर्मा",
  });

  const Q6_C_PLACEHOLDER = Text({
    tid: "SCHOOL_2026_PT2_Q6_C_PLACEHOLDER",
    def: "उदा॰ : वेदरक्षणनिधि ट्रस्ट",
  });

  const Q6_E_PLACEHOLDER = Text({
    tid: "SCHOOL_2026_PT2_Q6_E_PLACEHOLDER",
    def: "उदा॰ : उत्तीर्ण श्रेणी",
  });

  const Q7_A_PLACEHOLDER = Text({
    tid: "SCHOOL_2026_PT2_Q7_A_PLACEHOLDER",
    def: "उदा॰ : नोरि केदारेश्वर शर्मा",
  });

  const Q7_B_PLACEHOLDER = Text({
    tid: "SCHOOL_2026_PT2_Q7_B_PLACEHOLDER",
    def: "उदा॰ : भारतात्मा उत्कृष्ट वेदविद्यार्थी पुरस्कार",
  });

  const Q7_C_PLACEHOLDER = Text({
    tid: "SCHOOL_2026_PT2_Q7_C_PLACEHOLDER",
    def: "उदा॰ : सिंघल फाउण्डेशन, उदयपुर",
  });

  const Q8_A_PLACEHOLDER = Text({
    tid: "SCHOOL_2026_PT2_Q8_A_PLACEHOLDER",
    def: "उदा॰ : सागर शर्मा",
  });

  const Q8_D_PLACEHOLDER = Text({
    tid: "SCHOOL_2026_PT2_Q8_D_PLACEHOLDER",
    def: "उदा॰ : आदर्श वेदविद्यालय, उज्जैन",
  });

  const Q9_A_PLACEHOLDER = Text({
    tid: "SCHOOL_2026_PT2_Q9_A_PLACEHOLDER",
    def: "उदा॰ : भारतात्मा उत्तम वेदविद्यालय पुरस्कार",
  });

  const Q9_B_PLACEHOLDER = Text({
    tid: "SCHOOL_2026_PT2_Q9_B_PLACEHOLDER",
    def: "उदा॰ : सिंघल फाउण्डेशन, उदयपुर",
  });

  const SELECT_DATE = Text({
    tid: "SELECT_DATE",
    def: "MM-YYYY",
  });

  useEffect(() => {
    if (searchParams) {
      // Decrypt and set formId, userId, and formFor from URL search parameters
      const fId = EncryptUtils.decrypt(searchParams.get("id") || "");
      const uId = EncryptUtils.decrypt(searchParams.get("ui") || "");
      const fFor = EncryptUtils.decrypt(searchParams.get("ff") || "");
      setFormId(fId);
      setUserId(uId);
      setFormFor(fFor);
    }
    if (currentFormId) {
      setFormId(EncryptUtils.decrypt(currentFormId));
    }
    if (currentUserId) {
      setUserId(EncryptUtils.decrypt(currentUserId));
    }
    if (currentFormFor) {
      setFormFor(EncryptUtils.decrypt(currentFormFor));
    }
  }, [searchParams, currentFormId, currentUserId, currentFormFor]);

  const [rowCount, setRowCount] = useState({
    SCHOOL_2026_PT2_Q2: 1,
    SCHOOL_2026_PT2_Q3: 1,
    SCHOOL_2026_PT2_Q4: 1,
    SCHOOL_2026_PT2_Q5: 1,
    SCHOOL_2026_PT2_Q6: 1,
    SCHOOL_2026_PT2_Q7: 1,
    SCHOOL_2026_PT2_Q8: 1,
    SCHOOL_2026_PT2_Q9: 1,
    SCHOOL_2026_PT2_Q10: 1,
  });

  const inputRequiredRule: Rule[] = [
    {
      required: true,
      message: Text({ tid: "REQUIRED", def: "Required" }),
    },
  ];

  const [guruSignature, setGuruSignature] = useState<string | null>(null);
  const [isReviewMode, setIsReviewMode] = useState(false);

  const [documentsData, setDocumentsData] = useState<any>([]);

  const MULANT = Text({ tid: "MULANT", def: "MULANT" });
  const LEVEL_1 = Text({ tid: "LEVEL_1", def: "LEVEL_1" });
  const LEVEL_2 = Text({ tid: "LEVEL_2", def: "LEVEL_2" });
  const UCCHTAR = Text({ tid: "UCCHTAR", def: "UCCHTAR" });

  const SCHOOL_2026_PT2_Q2_A = Text({
    tid: "SCHOOL_2026_PT2_Q2_A",
    def: "Teacher Name",
  });

  const SCHOOL_2026_PT2_Q2_DOC = Text({
    tid: "SCHOOL_2026_PT2_Q2_DOC",
    def: "Document",
  });

  const SCHOOL_2026_PT2_Q9_A = Text({
    tid: "SCHOOL_2026_PT2_Q9_A",
    def: "Award/Honor Name",
  });

  const SCHOOL_2026_PT2_Q9_DOC = Text({
    tid: "SCHOOL_2026_PT2_Q9_DOC",
    def: "Document",
  });

  const SCHOOL_2026_PT2_Q12 = Text({
    tid: "SCHOOL_2026_PT2_Q12",
    def: "Signature of Head of Institution",
  });
  // Function to collect all uploaded document titles
  const getUploadedDocuments = () => {
    const documents: { documentDetails: string; documentUrl?: string }[] = [];

    // Collect from Q2 (Teacher documents)
    const q2Data = Array.isArray(form.getFieldValue("SCHOOL_2026_PT2_Q2"))
      ? form.getFieldValue("SCHOOL_2026_PT2_Q2")
      : [];

    q2Data.forEach((item: any) => {
      if (item?.SCHOOL_2026_PT2_Q2_DOC && item?.teacherName) {
        documents.push({
          documentDetails: `${SCHOOL_2026_PT2_Q2_A}: ${item?.teacherName}`,
          documentUrl: item?.SCHOOL_2026_PT2_Q2_DOC,
        });
      }
    });

    // Collect from Q9 (Awards/Honors documents)
    const q9Data = Array.isArray(form.getFieldValue("SCHOOL_2026_PT2_Q9"))
      ? form.getFieldValue("SCHOOL_2026_PT2_Q9")
      : [];

    q9Data.forEach((item: any) => {
      if (item?.SCHOOL_2026_PT2_Q9_DOC && item?.awardHonorName) {
        documents.push({
          documentDetails: `${SCHOOL_2026_PT2_Q9_A}: ${item?.awardHonorName}`,
          documentUrl: item?.SCHOOL_2026_PT2_Q9_DOC,
        });
      }
    });
    return documents;
  };

  useEffect(() => {
    const initialData = {
      SCHOOL_2026_PT2_Q2: [
        {
          teacherName: "",
          vedaShakha: null,
          vedicStudyLevel: null,
          age: "",
          fullTimeOrPartTime: null,
          SCHOOL_2026_PT2_Q2_DOC: "",
        },
      ],
      SCHOOL_2026_PT2_Q3: [
        {
          teacherName: "",
          vedaShakha: null,
          startingOfTeaching: null,
          endingOfTeaching: null,
          reasonOfLeaving: "",
        },
      ],
      SCHOOL_2026_PT2_Q4: [
        {
          teacherName: "",
          awardName: "",
          instituteName: "",
          year: "",
        },
      ],
      SCHOOL_2026_PT2_Q5: [
        {
          vedicStudentName: "",
          age: "",
          vedaBranch: null,
          studyLevel: null,
        },
      ],
      SCHOOL_2026_PT2_Q6: [
        {
          vedicStudentName: "",
          vedicStudyLevel: null,
          examinationInstitutionName: "",
          year: "",
          category: "",
        },
      ],
      SCHOOL_2026_PT2_Q7: [
        {
          vedicStudentName: "",
          awardName: "",
          institutionName: "",
          year: "",
        },
      ],
      SCHOOL_2026_PT2_Q8: [
        {
          formarStudentName: "",
          year: "",
          vedicBranch: null,
          currentInstitutionOfVedicTeaching: "",
        },
      ],
      SCHOOL_2026_PT2_Q9: [
        {
          awardHonorName: "",
          institutionName: "",
          year: "",
          SCHOOL_2026_PT2_Q9_DOC: "",
        },
      ],
      SCHOOL_2026_PT2_Q10: [
        {
          documentDetails: "",
        },
      ],
    };

    form.setFieldsValue(initialData);
  }, []);

  const handleFieldChange = async (
    field: string,
    value: any,
    answerType: number = eAnswerType.TEXT,
    bulkPayload?: any[],
    isSubmitted?: boolean,
  ) => {
    if (formId?.length) {
      try {
        setIsSubmitting(true);

        const answers: any[] = [];

        // Handle single field
        if (field.length > 0) {
          const trimmedValue = typeof value === "string" ? value.trim() : value;
          if (trimmedValue === null || trimmedValue === undefined) {
            form.setFieldValue(field, "");
            // Continue execution to save the empty string to backend
          }

          // Auto-detect answer type from JSON strings
          let detectedType = answerType;
          if (typeof trimmedValue === "string") {
            if (trimmedValue.startsWith("[")) {
              detectedType = eAnswerType.ARRAY;
            } else if (trimmedValue.startsWith("{")) {
              detectedType = eAnswerType.OBJECT;
            }
          }

          // Format answer based on type
          let formattedAnswer = trimmedValue;
          if (
            detectedType === eAnswerType.ARRAY ||
            detectedType === eAnswerType.OBJECT
          ) {
            formattedAnswer =
              typeof trimmedValue === "string"
                ? trimmedValue
                : JSON.stringify(trimmedValue);
          } else if (
            detectedType === eAnswerType.DATE &&
            typeof trimmedValue === "object" &&
            trimmedValue?.format
          ) {
            formattedAnswer = trimmedValue.format("YYYY-MM-DD");
          }

          answers.push({
            questionKey: field,
            answerType: detectedType,
            answer: formattedAnswer,
            part: eSTAGE.STAGE2,
          });
        }

        // Handle bulk payload (multiple answers)
        if (bulkPayload && Array.isArray(bulkPayload)) {
          answers.push(...bulkPayload);
        }

        const payload: any = {
          data: {
            id: formId,
            answers: answers,
          },
        };

        if (isSubmitted) {
          payload.data.isSubmitted = true;
        }

        const response = await post(updateField, payload);
        const { dataResponse } = response;
        const { returnCode, description } = dataResponse;
        if (returnCode == eResultCode.SUCCESS) {
          if (isSubmitted) {
            onShowToast({
              type: ToastType.success,
              title: <FaCheck />,
              position: ToastOpen.leftBottom,
              content: "Form Submitted Successfully!",
            });
            setTimeout(() => {
              navigate("/");
            }, 1000);
          }
        } else if (returnCode == eResultCode.INVALID_REQUEST) {
          onShowToast({
            type: ToastType.error,
            title: <ExclamationCircleOutlined />,
            position: ToastOpen.leftBottom,
            content: description,
          });
        }
      } catch (error) {
        console.error("School Part 2 Error : ", error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  // Initialize auto-save hook
  const { handleFormSubmit, onFormValuesChange, debouncedSave } =
    useFormAutoSave({
      formId,
      form,
      stage: eSTAGE.STAGE2,
      handleFieldChange,
      excludeFields: ["SCHOOL_2026_PT2_Q13"], // Add any fields that shouldn't auto-save
    });

  const q2WatchData = Form.useWatch("SCHOOL_2026_PT2_Q2", form);
  const q9WatchData = Form.useWatch("SCHOOL_2026_PT2_Q9", form);

  useEffect(() => {
    const uploadedDocuments = getUploadedDocuments();
    form.setFieldsValue({
      SCHOOL_2026_PT2_Q10: uploadedDocuments,
    });
    debouncedSave("SCHOOL_2026_PT2_Q10", JSON.stringify(uploadedDocuments));
  }, [q2WatchData, q9WatchData]);

  // Centralized handler for local side-effects + auto-save
  const handleLocalFormChange = (changedValues: any, allValues: any) => {
    // 1. Run Auto Save Logic from hook
    onFormValuesChange(changedValues, allValues);
  };

  /**
   * Handle "Next" button click - validates form and enters review mode
   */
  const handleNextClick = async () => {
    try {
      await form.validateFields();
      setIsReviewMode(true);
      // Scroll to top so user can review from beginning
      document.getElementById("schoolFormPart2")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    } catch (errorInfo: any) {
      const firstError = errorInfo?.errorFields?.[0];

      if (firstError) {
        form.scrollToField(firstError.name, {
          behavior: "smooth",
          block: "center",
        });
      }
    }
  };

  /**
   * Handle "Back" button click - exits review mode to allow editing
   */
  const handleBackClick = () => {
    setIsReviewMode(false);
  };

  /**
   * Helper function to safely parse and map form data from API response
   * Handles type conversion, date parsing, and nested objects/arrays
   */
  const getMappedData = (data: any) => {
    if (!data) return {};

    const getRollingFiveYearData = (prevData: any[], emptyRowSkeleton: any) => {
      try {
        const previousYearData = Array.isArray(prevData) ? prevData : [];

        // Take only first 4 rows from previous year (drop the 5th/oldest year)
        const shiftedData = previousYearData.slice(0, 4);

        // Prepend new current year row
        return [emptyRowSkeleton, ...shiftedData];
      } catch (error) {
        console.error("Error mapping rolling 5-year data:", error);
        return [emptyRowSkeleton];
      }
    };

    const mappedData: { [key: string]: any } = {
      // Name - Q1
      SCHOOL_2026_PT2_Q1: data.SCHOOL_PT2_Q1_NAME || "",

      // Q2 - Current Teachers (Direct Map)
      SCHOOL_2026_PT2_Q2: data.SCHOOL_PT2_Q2 || [],

      // Q3 - Past Teachers (Rolling 5 years)
      SCHOOL_2026_PT2_Q3: getRollingFiveYearData(data.SCHOOL_PT2_Q3, {
        teacherName: "",
        vedaShakha: null,
        startingOfTeaching: "",
        endingOfTeaching: "",
        reasonOfLeaving: "",
      }),

      // Q4 - Teacher Awards (Rolling 5 years)
      SCHOOL_2026_PT2_Q4: getRollingFiveYearData(data.SCHOOL_PT2_Q4, {
        teacherName: "",
        awardName: "",
        instituteName: "",
        year: dayjs().year(CURRENT_YEAR),
      }),

      // Q5 - Current Students (Direct Map)
      SCHOOL_2026_PT2_Q5: data.SCHOOL_PT2_Q5 || [],

      // Q6 - Student Exams (Rolling 5 years)
      SCHOOL_2026_PT2_Q6: getRollingFiveYearData(data.SCHOOL_PT2_Q6, {
        vedicStudentName: "",
        vedicStudyLevel: null,
        examinationInstitutionName: "",
        year: dayjs().year(CURRENT_YEAR),
        category: "",
      }),

      // Q7 - Student Awards (Rolling 5 years)
      SCHOOL_2026_PT2_Q7: getRollingFiveYearData(data.SCHOOL_PT2_Q7, {
        vedicStudentName: "",
        awardName: "",
        institutionName: "",
        year: dayjs().year(CURRENT_YEAR),
      }),

      // Q8 - Former Students (Direct Map)
      SCHOOL_2026_PT2_Q8: data.SCHOOL_PT2_Q8 || [],

      // Q9 - Institution Awards (Rolling 5 years)
      SCHOOL_2026_PT2_Q9: getRollingFiveYearData(data.SCHOOL_PT2_Q9, {
        awardHonorName: "",
        institutionName: "",
        year: dayjs().year(CURRENT_YEAR),
        SCHOOL_2026_PT2_Q9_DOC: "",
      }),

      // Q16 - Documents (Direct Map)
      // SCHOOL_2026_PT2_Q10: data.SCHOOL_PT2_Q16 || [],

      SCHOOL_2026_PT2_Q11: data.SCHOOL_PT2_Q10 || [],
      SCHOOL_2026_PT2_Q12: data.SCHOOL_PT2_Q11 || [],
      SCHOOL_2026_PT2_Q14: data.SCHOOL_PT2_Q13 || [],
      // SCHOOL_2026_PT2_Q15: data.SCHOOL_PT2_Q16 || [],
      SCHOOL_2026_PT2_Q16: data.SCHOOL_PT2_Q16 || [],
    };

    // Filter out null/undefined values
    return Object.fromEntries(
      Object.entries(mappedData).filter(
        ([_, value]) => value !== null && value !== undefined && value !== "",
      ),
    );
  };

  // Helper function to serialize table data
  const serializeTableData = (tableData: any[]) => {
    return JSON.stringify(
      tableData.map((row) => {
        const newRow = { ...row };
        Object.keys(newRow).forEach((key) => {
          if (dayjs.isDayjs(newRow[key])) {
            if (key === "year") {
              newRow[key] = newRow[key].format("YYYY");
            } else if (
              ["startingOfTeaching", "endingOfTeaching"].includes(key)
            ) {
              newRow[key] = newRow[key].format("MM/YYYY");
            } else {
              // Default fallback for other potential date fields
              newRow[key] = newRow[key].format("YYYY-MM-DD");
            }
          }
        });
        return newRow;
      }),
    );
  };

  // Generic function to remove a row
  const removeRow = async (form: any, key: string, index: number) => {
    const currentData = form.getFieldValue(key) || [];
    const updatedData = currentData.filter((_: any, i: number) => i !== index);
    form.setFieldsValue({
      [key]: updatedData,
    });
    setRowCount((prev) => ({ ...prev, [key]: updatedData.length }));
    await handleFieldChange(key, serializeTableData(updatedData));

    // Update Q10 summary table if the removed row was from a table with documents
    const tablesWithDocuments = ["SCHOOL_2026_PT2_Q2", "SCHOOL_2026_PT2_Q9"];
    if (tablesWithDocuments.includes(key)) {
      // Small delay to ensure form state is updated
      setTimeout(() => {
        const uploadedDocuments = getUploadedDocuments();
        form.setFieldsValue({
          SCHOOL_2026_PT2_Q10: uploadedDocuments,
        });
      }, 100);
    }
  };

  const handleDateChange = (
    form: any,
    fieldName: string,
    index: number,
    dateString: string,
    handleFieldChange: (field: string, value: any) => void,
  ) => {
    // We update the form state with the Dayjs object (managed by Antd Form Item)
    // But we trigger the save with the formatted string
    const currentData = form.getFieldValue(fieldName) || [];
    // The form state might already be updated by the DatePicker onChange before this callback (if using standard Antd pattern)
    // But to be safe and consistent with older code, let's re-read and ensure we serialize correctly.

    // Note: The `date` argument in onChange is the Dayjs object, `dateString` is the formatted string.
    // If we want to store the Dayjs object in the form state (for the UI to work), we should do that.
    // But here we might be expected to save the whole table data string.

    // Actually, usually Antd Form updates the value automatically if name is set.
    // We just need to trigger the API save with the serialized data.

    // Let's defer to the next render cycle or just use current data
    // Waiting for next tick to ensure form state is updated by Antd
    setTimeout(() => {
      const updatedData = form.getFieldValue(fieldName) || [];
      handleFieldChange(fieldName, serializeTableData(updatedData));
    }, 0);
  };

  const getFormData = async () => {
    setIsLoading(true);
    try {
      const payload: any = {
        data: {
          id: formId,
          part: eSTAGE.STAGE2,
          isPtOne: false,
          year: CURRENT_YEAR,
          userFormType: eUSER_FORM_TYPE.SCHOOL,
          userId: userId,
          formFor: formFor,
        },
      };

      const response = await post(GetFormData, payload);
      const { dataResponse, data } = response;
      const { returnCode } = dataResponse;

      if (returnCode == eResultCode.SUCCESS && Array.isArray(data)) {
        // Convert array of answers to form field values
        const formattedData: { [key: string]: any } = {};
        const docs: any[] = [];

        data.forEach((answer: any) => {
          const { questionKey, answerType, answer: answerValue } = answer;

          if (!questionKey) return;

          // Parse based on answerType
          switch (answerType) {
            case eAnswerType.ARRAY:
            case eAnswerType.OBJECT:
              try {
                formattedData[questionKey] = answerValue
                  ? JSON.parse(answerValue)
                  : answerType === eAnswerType.ARRAY
                    ? []
                    : {};
              } catch (error) {
                console.error(`Error parsing ${questionKey}:`, error);
                formattedData[questionKey] =
                  answerType === eAnswerType.ARRAY ? [] : {};
              }
              break;
            case eAnswerType.FILE:
              // File fields store document ID, don't set in form
              formattedData[questionKey] = answerValue || "";
              if (answerValue) {
                docs.push({
                  docPath: answerValue,
                  labelName: questionKey, // linking doc to question
                });
              }
              break;
            case eAnswerType.DATE:
              // Convert date string to dayjs object for DatePicker
              formattedData[questionKey] = Utils.parseToDayjs(answerValue);
              break;
            case eAnswerType.TEXT:
            default:
              formattedData[questionKey] = answerValue || "";

              // Force Date Conversion for known date fields if they come as TEXT
              if (DATE_FIELD_KEYS.includes(questionKey) && answerValue) {
                formattedData[questionKey] = Utils.parseToDayjs(answerValue);
              }
              break;
          }
        });

        // Convert year fields in table data to dayjs objects for DatePicker

        // Fields containing year dates (YYYY)
        const JSONwithYearFields = [
          "SCHOOL_2026_PT2_Q4",
          "SCHOOL_2026_PT2_Q6",
          "SCHOOL_2026_PT2_Q7",
          "SCHOOL_2026_PT2_Q8",
          "SCHOOL_2026_PT2_Q9",
        ];

        // Fields containing month-year dates (MM-YYYY)
        const JSONwithDateFields = ["SCHOOL_2026_PT2_Q3"];

        JSONwithYearFields.forEach((tableKey) => {
          if (Array.isArray(formattedData[tableKey])) {
            formattedData[tableKey] = formattedData[tableKey].map(
              (row: any) => {
                if (row.year) {
                  const resultDate = Utils.parseToDayjs(row.year);
                  return { ...row, year: resultDate };
                }
                return row;
              },
            );
          }
        });

        JSONwithDateFields.forEach((tableKey) => {
          if (Array.isArray(formattedData[tableKey])) {
            formattedData[tableKey] = formattedData[tableKey].map(
              (row: any) => {
                const newRow = { ...row };
                ["startingOfTeaching", "endingOfTeaching"].forEach(
                  (dateKey) => {
                    const val = newRow[dateKey];
                    if (val) {
                      const resultDate = Utils.parseToDayjs(val);
                      newRow[dateKey] = resultDate;
                    }
                  },
                );
                return newRow;
              },
            );
          }
        });

        if (docs.length > 0) {
          setDocumentsData(docs);
        }

        if (Object.keys(formattedData).length > 0) {
          form.setFieldsValue(formattedData);
          setTimeout(() => {
            const uploadedDocuments = getUploadedDocuments();
            form.setFieldsValue({
              SCHOOL_2026_PT2_Q10: uploadedDocuments,
            });
          }, 100);

          // Sync affidavitAccepted state with loaded form data
          if (
            formattedData.affidavitAccepted ||
            formattedData.affidavitAccepted === "1"
          ) {
            form.setFieldValue("affidavitAccepted", true);
          }
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getPreviousFormData = async () => {
    setIsLoading(true);
    try {
      const payload: any = {
        data: {
          part: eSTAGE.STAGE2,
          year: CURRENT_YEAR - 1,
          userFormType: eUSER_FORM_TYPE.SCHOOL,
          userId: userId,
          formFor: formFor,
        },
      };

      const response = await post(GetFormData, payload);
      const { dataResponse, data } = response;
      const { returnCode } = dataResponse;

      if (returnCode == eResultCode.SUCCESS) {
        const formData = Object.values(data?.schoolPt2Forms || {}).every(
          (value) => value === null,
        )
          ? {}
          : data?.schoolPt2Forms || {};

        // List of fields to exclude (file input fields)
        const jsonStringFields = [
          "SCHOOL_2026_PT2_Q2",
          "SCHOOL_PT2_Q2",
          "SCHOOL_2026_PT2_Q3",
          "SCHOOL_2026_PT2_Q4",
          "SCHOOL_2026_PT2_Q5",
          "SCHOOL_PT2_Q5",
          "SCHOOL_2026_PT2_Q6",
          "SCHOOL_2026_PT2_Q7",
          "SCHOOL_2026_PT2_Q8",
          "SCHOOL_PT2_Q8",
          "SCHOOL_2026_PT2_Q9",
          "SCHOOL_2026_PT2_Q10",
          "SCHOOL_PT2_Q10",
          "SCHOOL_PT2_Q16",
        ];

        const fileFields = ["SCHOOL_2026_PT2_Q12"];

        const JSONwithYearFields = [
          "SCHOOL_2026_PT2_Q4",
          "SCHOOL_2026_PT2_Q6",
          "SCHOOL_2026_PT2_Q7",
          "SCHOOL_2026_PT2_Q8",
          "SCHOOL_PT2_Q8",
          "SCHOOL_2026_PT2_Q9",
        ];

        // Fields containing month-year dates (MM-YYYY)
        const JSONwithDateFields = ["SCHOOL_2026_PT2_Q3"];

        const formattedData = Object.keys(formData).reduce(
          (acc: { [key: string]: any }, key) => {
            if (jsonStringFields.includes(key)) {
              try {
                const parsedArr = formData[key]
                  ? JSON.parse(formData[key])
                  : [{}];

                if (JSONwithYearFields.includes(key)) {
                  parsedArr.forEach((item: any) => {
                    const val = item.year;
                    if (val) {
                      item.year = Utils.parseToDayjs(val);
                    }
                  });
                }

                if (JSONwithDateFields.includes(key)) {
                  parsedArr.forEach((item: any) => {
                    ["startingOfTeaching", "endingOfTeaching"].forEach(
                      (dateKey) => {
                        const val = item[dateKey];
                        if (val) {
                          item[dateKey] = Utils.parseToDayjs(val);
                        }
                      },
                    );
                  });
                }

                acc[key] = parsedArr;
              } catch (error) {
                console.error(error);
              }
            } else if (fileFields.includes(key)) {
              acc[key] = "";
            } else {
              let value = formData[key];
              // Force Date Conversion for known date fields
              if (DATE_FIELD_KEYS.includes(key) && value) {
                value = Utils.parseToDayjs(value);
              }
              acc[key] = value;
            }
            return acc;
          },
          {},
        );

        const mappedData = getMappedData(formattedData);

        // Use mapped data (with 2026 field names) instead of original formattedData
        // mappedData.SCHOOL_2026_PT2_Q10 = [];

        // Q12 - Signature of Head of Institution (file upload)
        // mappedData.SCHOOL_2026_PT2_Q12 = undefined;

        // Q14 - Affidavit Acceptance Checkbox
        // mappedData.SCHOOL_2026_PT2_Q14 = undefined;

        // Q15 - Affidavit Place
        // mappedData.SCHOOL_2026_PT2_Q15 = undefined;

        // Q16 - Affidavit Date
        // Reset to today's date
        // mappedData.SCHOOL_2026_PT2_Q16 = dayjs();

        // ------------------------------------------------------------------
        // Clear uploaded documents inside table rows
        // ------------------------------------------------------------------

        // Q2 - Teacher documents
        // if (Array.isArray(mappedData.SCHOOL_2026_PT2_Q2)) {
        //   mappedData.SCHOOL_2026_PT2_Q2 = mappedData.SCHOOL_2026_PT2_Q2.map(
        //     (row: any) => ({
        //       ...row,
        //       SCHOOL_2026_PT2_Q2_DOC: "",
        //     }),
        //   );
        // }

        // Q9 - Institution award documents
        // if (Array.isArray(mappedData.SCHOOL_2026_PT2_Q9)) {
        //   mappedData.SCHOOL_2026_PT2_Q9 = mappedData.SCHOOL_2026_PT2_Q9.map(
        //     (row: any) => ({
        //       ...row,
        //       SCHOOL_2026_PT2_Q9_DOC: "",
        //     }),
        //   );
        // }

        if (mappedData && Object.keys(mappedData).length > 0) {
          form.setFieldsValue(mappedData);
          setTimeout(() => {
            const uploadedDocuments = getUploadedDocuments();
            form.setFieldsValue({
              SCHOOL_2026_PT2_Q10: uploadedDocuments,
            });
          }, 100);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const autoFillPartOneData = async () => {
    setIsLoading(true);
    try {
      const payload: any = {
        data: {
          id: formId,
          part: eSTAGE.STAGE1,
          isPtOne: true,
          year: CURRENT_YEAR,
          userFormType: eUSER_FORM_TYPE.SCHOOL,
          userId: userId,
          formFor: formFor,
        },
      };
      const response = await post(GetFormData, payload);
      const { dataResponse, data } = response;
      const { returnCode } = dataResponse;
      if (returnCode == eResultCode.SUCCESS) {
        if (data && Object.keys(data).length > 0) {
          const name = data.find(
            (item: any) => item.questionKey === "SCHOOL_2026_PT1_Q1_A",
          )?.answer;
          form.setFieldsValue({
            SCHOOL_2026_PT2_Q1: name,
          });
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (formId && formFor && user) {
      const fetchData = async () => {
        await getPreviousFormData();
        await getFormData();
        await autoFillPartOneData();
      };
      fetchData();
    }
  }, [formId, formFor, userId, user]);

  // Auto-update Q16 document list whenever documents are uploaded
  const q2Data = Form.useWatch("SCHOOL_2026_PT2_Q2", form);
  const q9Data = Form.useWatch("SCHOOL_2026_PT2_Q9", form);

  useEffect(() => {
    const uploadedDocuments = getUploadedDocuments();
    form.setFieldsValue({
      SCHOOL_2026_PT2_Q10: uploadedDocuments,
    });
  }, [q2Data, q9Data]); // Update when any document field changes

  // Q2 -----------------------------------------Q2-TABLE-COLUMNS-AND-DATA-SOURCE-----------------------------------------
  const SCHOOL_2026_PT2_Q2: TableProps<SCHOOL_2026_PT2_Q2>["columns"] = [
    {
      title: Text({ tid: "SERIAL_NUMBER", def: "SERIAL_NUMBER" }),
      dataIndex: "serialNumber",
      key: "serialNumber",
      width: "100px",
      render: (_, record, index) => getNumberInLanguage(index + 1),
    },
    {
      title: Text({ tid: "SCHOOL_2026_PT2_Q2_A", def: "Teacher's name" }),
      dataIndex: "teacherName",
      key: "teacherName",
      render: (_, record, index) => {
        const docUrl = form.getFieldValue([
          "SCHOOL_2026_PT2_Q2",
          index,
          "SCHOOL_2026_PT2_Q2_DOC",
        ]);
        return (
          <Form.Item
            name={["SCHOOL_2026_PT2_Q2", index, "teacherName"]}
            rules={inputRequiredRule}
          >
            <Input
              disabled={disabled || !!docUrl || isReviewMode}
              placeholder={Q2_A_PLACEHOLDER}
            />
          </Form.Item>
        );
      },
    },
    {
      title: Text({ tid: "SCHOOL_2026_PT2_Q2_B", def: "Veda Shakha" }),
      dataIndex: "vedaShakha",
      key: "vedaShakha",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_2026_PT2_Q2", index, "vedaShakha"]}
          rules={inputRequiredRule}
        >
          <Select
            disabled={disabled || isReviewMode}
            placeholder={Text({
              tid: "SELECT_VEDA_SHAKHA",
              def: "Select Veda Shakha",
            })}
            showSearch
            optionFilterProp="children"
          >
            {Object.values(vedMapping).flatMap((veda) =>
              veda.vedashakhas.map((shakha, idx) => (
                <Select.Option key={`${shakha}-${idx}`} value={shakha}>
                  {shakha}
                </Select.Option>
              )),
            )}
          </Select>
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "SCHOOL_2026_PT2_Q2_C", def: "Ved study level" }),
      dataIndex: "vedicStudyLevel",
      key: "vedicStudyLevel",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_2026_PT2_Q2", index, "vedicStudyLevel"]}
          rules={inputRequiredRule}
        >
          <Select
            disabled={disabled || isReviewMode}
            placeholder={DROPDOWN_PLACEHOLDER}
          >
            <Select.Option value={MULANT}>{MULANT}</Select.Option>
            <Select.Option value={LEVEL_1}>{LEVEL_1}</Select.Option>
            <Select.Option value={LEVEL_2}>{LEVEL_2}</Select.Option>
            <Select.Option value={UCCHTAR}>{UCCHTAR}</Select.Option>
          </Select>
        </Form.Item>
      ),
    },
    {
      title: Text({
        tid: "SCHOOL_2026_PT2_Q2_D",
        def: "Age",
      }),
      dataIndex: "age",
      key: "age",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_2026_PT2_Q2", index, "age"]}
          rules={numberInputRules({
            required: true,
            allowNegative: false,
            min: 0,
            max: 120,
          })}
        >
          <Input
            type="number"
            disabled={disabled || isReviewMode}
            placeholder={Q2_D_PLACEHOLDER}
          />
        </Form.Item>
      ),
    },
    {
      title: Text({
        tid: "SCHOOL_2026_PT2_Q2_E",
        def: "Full-time / part-time",
      }),
      dataIndex: "fullTimeOrPartTime",
      key: "fullTimeOrPartTime",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_2026_PT2_Q2", index, "fullTimeOrPartTime"]}
          rules={inputRequiredRule}
        >
          <Select
            disabled={disabled || isReviewMode}
            placeholder={DROPDOWN_PLACEHOLDER}
          >
            <Select.Option value={eFullTimeOrPartTime.FULLTIME}>
              {Text({ tid: "FULLTIME", def: "FULLTIME" })}
            </Select.Option>
            <Select.Option value={eFullTimeOrPartTime.PARTTIME}>
              {Text({ tid: "PARTTIME", def: "PARTTIME" })}
            </Select.Option>
          </Select>
        </Form.Item>
      ),
      width: "200px",
    },
    {
      title: Text({ tid: "ATTACHED_DOCUMENT", def: "Document Upload" }),
      dataIndex: "documentUpload",
      key: "documentUpload",
      width: "20%",
      render: (_: any, record: any, index: number) => {
        const docUrl = form.getFieldValue([
          "SCHOOL_2026_PT2_Q2",
          index,
          "SCHOOL_2026_PT2_Q2_DOC",
        ]);
        const teacherName = form.getFieldValue([
          "SCHOOL_2026_PT2_Q2",
          index,
          "teacherName",
        ]);
        return (
          <DocumentUploadTableField
            formId={formId || ""}
            userId={userId || ""}
            labelName={`SCHOOL_2026_PT2_Q2_${index}`}
            existingDocumentUrl={docUrl}
            disabled={disabled || !teacherName || isReviewMode}
            onUploadComplete={(docUrl) => {
              form.setFieldValue(
                ["SCHOOL_2026_PT2_Q2", index, "SCHOOL_2026_PT2_Q2_DOC"],
                docUrl,
              );
              handleFieldChange(
                "SCHOOL_2026_PT2_Q2",
                JSON.stringify(form.getFieldValue("SCHOOL_2026_PT2_Q2")),
              );
              // Update Q16 summary table
              setTimeout(async () => {
                const uploadedDocuments = getUploadedDocuments();
                form.setFieldsValue({
                  SCHOOL_2026_PT2_Q10: uploadedDocuments,
                });
                await handleFieldChange(
                  "SCHOOL_2026_PT2_Q10",
                  JSON.stringify(uploadedDocuments),
                );
              }, 100);
            }}
          />
        );
      },
    },
    {
      title: Text({ tid: "ACTION", def: "ACTION" }),
      dataIndex: "action",
      width: "100px",

      key: "action",
      render: (_, record, index) => (
        <Form.Item>
          <Button
            disabled={disabled || isReviewMode}
            variant="filled"
            onClick={() => removeRow(form, "SCHOOL_2026_PT2_Q2", index)}
            className="actionButton"
          >
            {Text({ tid: "REMOVE", def: "remove" })}
          </Button>
        </Form.Item>
      ),
    },
  ];

  const endDateValiationsMessage = Text({
    tid: "END_DATE_BEFORE_START_DATE",
    def: "End date cannot be before start date",
  }) as string;
  // Q3 -----------------------------------------Q3-TABLE-COLUMNS-AND-DATA-SOURCE-----------------------------------------
  const SCHOOL_2026_PT2_Q3: TableProps<SCHOOL_2026_PT2_Q3>["columns"] = [
    {
      title: Text({ tid: "SERIAL_NUMBER", def: "SERIAL_NUMBER" }),
      dataIndex: "serialNumber",
      key: "serialNumber",
      width: "100px",
      render: (_, record, index) => getNumberInLanguage(index + 1),
    },
    {
      title: Text({ tid: "SCHOOL_2026_PT2_Q3_A", def: "Teacher's name" }),
      dataIndex: "teacherName",
      key: "teacherName",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_2026_PT2_Q3", index, "teacherName"]}
          rules={inputRequiredRule}
        >
          <Input
            disabled={disabled || isReviewMode}
            placeholder={Q3_A_PLACEHOLDER}
          />
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "SCHOOL_2026_PT2_Q3_B", def: "Veda Shakha" }),
      dataIndex: "vedaShakha",
      key: "vedaShakha",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_2026_PT2_Q3", index, "vedaShakha"]}
          rules={inputRequiredRule}
        >
          <Select
            disabled={disabled || isReviewMode}
            placeholder={Text({
              tid: "SELECT_VEDA_SHAKHA",
              def: "Select Veda Shakha",
            })}
            showSearch
            optionFilterProp="children"
          >
            {Object.values(vedMapping).flatMap((veda) =>
              veda.vedashakhas.map((shakha, idx) => (
                <Select.Option key={`${shakha}-${idx}`} value={shakha}>
                  {shakha}
                </Select.Option>
              )),
            )}
          </Select>
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "TEACHING_DURATION", def: "TEACHING_DURATION" }),
      children: [
        {
          title: Text({
            tid: "SCHOOL_2026_PT2_Q3_C",
            def: "Starting of Teaching",
          }),
          dataIndex: "startingOfTeaching",
          key: "startingOfTeaching",
          width: "200px",
          render: (_, record, index) => (
            <Form.Item
              name={["SCHOOL_2026_PT2_Q3", index, "startingOfTeaching"]}
              rules={inputRequiredRule}
            >
              <DatePicker
                disabled={disabled || isReviewMode}
                picker="month"
                format="MM-YYYY"
                disabledDate={(current) =>
                  current && current > dayjs().endOf("month")
                }
                placeholder={SELECT_DATE}
                style={{ width: "100%" }}
              />
            </Form.Item>
          ),
        },
        {
          title: Text({
            tid: "SCHOOL_2026_PT2_Q3_D",
            def: "Ending of Teaching",
          }),
          dataIndex: "endingOfTeaching",
          key: "endingOfTeaching",
          width: "200px",
          render: (_, record, index) => (
            <Form.Item
              name={["SCHOOL_2026_PT2_Q3", index, "endingOfTeaching"]}
              dependencies={[
                ["SCHOOL_2026_PT2_Q3", index, "startingOfTeaching"],
              ]}
              rules={[
                ...inputRequiredRule,
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const startDate = getFieldValue([
                      "SCHOOL_2026_PT2_Q3",
                      index,
                      "startingOfTeaching",
                    ]);
                    if (
                      !value ||
                      !startDate ||
                      value.isAfter(startDate) ||
                      value.isSame(startDate, "month")
                    ) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error(endDateValiationsMessage));
                  },
                }),
              ]}
            >
              <DatePicker
                disabled={disabled || isReviewMode}
                picker="month"
                format="MM-YYYY"
                disabledDate={(current) =>
                  current && current > dayjs().endOf("month")
                }
                placeholder={SELECT_DATE}
                style={{ width: "100%" }}
              />
            </Form.Item>
          ),
        },
      ],
    },

    {
      title: Text({ tid: "SCHOOL_2026_PT2_Q3_E", def: "Reason of Leaving" }),
      dataIndex: "reasonOfLeaving",
      key: "reasonOfLeaving",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_2026_PT2_Q3", index, "reasonOfLeaving"]}
          rules={inputRequiredRule}
        >
          <Input
            disabled={disabled || isReviewMode}
            placeholder={Q3_E_PLACEHOLDER}
          />
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "ACTION", def: "ACTION" }),
      dataIndex: "action",
      width: "100px",

      key: "action",
      render: (_, record, index) => (
        <Form.Item>
          <Button
            disabled={disabled || isReviewMode}
            variant="filled"
            onClick={() => removeRow(form, "SCHOOL_2026_PT2_Q3", index)}
            className="actionButton"
          >
            {Text({ tid: "REMOVE", def: "remove" })}
          </Button>
        </Form.Item>
      ),
    },
  ];

  // Q4 -----------------------------------------Q4-TABLE-COLUMNS-AND-DATA-SOURCE-----------------------------------------
  const SCHOOL_2026_PT2_Q4: TableProps<SCHOOL_2026_PT2_Q4>["columns"] = [
    {
      title: Text({ tid: "SERIAL_NUMBER", def: "SERIAL_NUMBER" }),
      dataIndex: "serialNumber",
      key: "serialNumber",
      width: "100px",
      render: (_, record, index) => getNumberInLanguage(index + 1),
    },
    {
      title: Text({ tid: "SCHOOL_2026_PT2_Q4_A", def: "Teacher's name" }),
      dataIndex: "teacherName",
      key: "teacherName",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_2026_PT2_Q4", index, "teacherName"]}
          rules={inputRequiredRule}
        >
          <Input
            disabled={disabled || isReviewMode}
            placeholder={Q4_A_PLACEHOLDER}
          />
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "SCHOOL_2026_PT2_Q4_B", def: "Award Name" }),
      dataIndex: "awardName",
      key: "awardName",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_2026_PT2_Q4", index, "awardName"]}
          rules={inputRequiredRule}
        >
          <Input
            disabled={disabled || isReviewMode}
            placeholder={Q4_B_PLACEHOLDER}
          />
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "SCHOOL_2026_PT2_Q4_C", def: "Institute Name" }),
      dataIndex: "instituteName",
      key: "instituteName",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_2026_PT2_Q4", index, "instituteName"]}
          rules={inputRequiredRule}
        >
          <Input
            disabled={disabled || isReviewMode}
            placeholder={Q4_C_PLACEHOLDER}
          />
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "SCHOOL_2026_PT2_Q4_D", def: "Year" }),
      dataIndex: "year",
      key: "year",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_2026_PT2_Q4", index, "year"]}
          rules={inputRequiredRule}
        >
          <DatePicker
            placeholder={Text({
              tid: "SELECT_YEAR",
              def: "Select Year",
            })}
            disabled={disabled || isReviewMode}
            disabledDate={disableFutureYears}
            picker="year"
            format="YYYY"
            onKeyDown={(e) => e.preventDefault()}
            onChange={(date, dateString) =>
              handleDateChange(
                form,
                "SCHOOL_2026_PT2_Q4",
                index,
                Array.isArray(dateString) ? dateString[0] : dateString,
                handleFieldChange,
              )
            }
          />
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "ACTION", def: "ACTION" }),
      dataIndex: "action",
      width: "100px",

      key: "action",
      render: (_, record, index) => (
        <Form.Item>
          <Button
            disabled={disabled || isReviewMode}
            variant="filled"
            onClick={() => removeRow(form, "SCHOOL_2026_PT2_Q4", index)}
            className="actionButton"
          >
            {Text({ tid: "REMOVE", def: "remove" })}
          </Button>
        </Form.Item>
      ),
    },
  ];

  // Q5 -----------------------------------------Q5-TABLE-COLUMNS-AND-DATA-SOURCE-----------------------------------------
  const SCHOOL_2026_PT2_Q5: TableProps<SCHOOL_2026_PT2_Q5>["columns"] = [
    {
      title: Text({ tid: "SERIAL_NUMBER", def: "SERIAL_NUMBER" }),
      dataIndex: "serialNumber",
      key: "serialNumber",
      width: "100px",
      render: (_, record, index) => getNumberInLanguage(index + 1),
    },
    {
      title: Text({ tid: "SCHOOL_2026_PT2_Q5_A", def: "Ved student's Name" }),
      dataIndex: "vedicStudentName",
      key: "vedicStudentName",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_2026_PT2_Q5", index, "vedicStudentName"]}
          rules={inputRequiredRule}
        >
          <Input
            disabled={disabled || isReviewMode}
            placeholder={Q5_A_PLACEHOLDER}
          />
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "SCHOOL_2026_PT2_Q5_B", def: "Age" }),
      dataIndex: "age",
      key: "age",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_2026_PT2_Q5", index, "age"]}
          rules={numberInputRules({
            required: true,
            allowNegative: false,
            min: 0,
            max: 120,
          })}
        >
          <Input
            type="number"
            disabled={disabled || isReviewMode}
            placeholder={Q5_B_PLACEHOLDER}
          />
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "SCHOOL_2026_PT2_Q5_C", def: "Veda Branch" }),
      dataIndex: "vedaBranch",
      key: "vedaBranch",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_2026_PT2_Q5", index, "vedaBranch"]}
          rules={inputRequiredRule}
        >
          <Select
            disabled={disabled || isReviewMode}
            placeholder={Text({
              tid: "SELECT_VEDA_SHAKHA",
              def: "Select Veda Shakha",
            })}
            showSearch
            optionFilterProp="children"
          >
            {Object.values(vedMapping).flatMap((veda) =>
              veda.vedashakhas.map((shakha, idx) => (
                <Select.Option key={`${shakha}-${idx}`} value={shakha}>
                  {shakha}
                </Select.Option>
              )),
            )}
          </Select>
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "SCHOOL_2026_PT2_Q5_D", def: "Study level" }),
      dataIndex: "studyLevel",
      key: "studyLevel",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_2026_PT2_Q5", index, "studyLevel"]}
          rules={inputRequiredRule}
        >
          <Select
            disabled={disabled || isReviewMode}
            placeholder={DROPDOWN_PLACEHOLDER}
          >
            <Select.Option value={MULANT}>{MULANT}</Select.Option>
            <Select.Option value={LEVEL_1}>{LEVEL_1}</Select.Option>
            <Select.Option value={LEVEL_2}>{LEVEL_2}</Select.Option>
            <Select.Option value={UCCHTAR}>{UCCHTAR}</Select.Option>
          </Select>
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "ACTION", def: "ACTION" }),
      dataIndex: "action",
      width: "100px",

      key: "action",
      render: (_, record, index) => (
        <Form.Item>
          <Button
            disabled={disabled || isReviewMode}
            variant="filled"
            onClick={() => removeRow(form, "SCHOOL_2026_PT2_Q5", index)}
            className="actionButton"
          >
            {Text({ tid: "REMOVE", def: "remove" })}
          </Button>
        </Form.Item>
      ),
    },
  ];

  // Q6 -----------------------------------------Q6-TABLE-COLUMNS-AND-DATA-SOURCE-----------------------------------------
  const SCHOOL_2026_PT2_Q6: TableProps<SCHOOL_2026_PT2_Q6>["columns"] = [
    {
      title: Text({ tid: "SERIAL_NUMBER", def: "SERIAL_NUMBER" }),
      dataIndex: "serialNumber",
      key: "serialNumber",
      width: "100px",
      render: (_, record, index) => getNumberInLanguage(index + 1),
    },
    {
      title: Text({ tid: "SCHOOL_2026_PT2_Q6_A", def: "Ved student's Name" }),
      dataIndex: "vedicStudentName",
      key: "vedicStudentName",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_2026_PT2_Q6", index, "vedicStudentName"]}
          rules={inputRequiredRule}
        >
          <Input
            disabled={disabled || isReviewMode}
            placeholder={Q6_A_PLACEHOLDER}
          />
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "SCHOOL_2026_PT2_Q6_B", def: "Ved study level" }),
      dataIndex: "vedicStudyLevel",
      key: "vedicStudyLevel",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_2026_PT2_Q6", index, "vedicStudyLevel"]}
          rules={inputRequiredRule}
        >
          <Select
            disabled={disabled || isReviewMode}
            placeholder={DROPDOWN_PLACEHOLDER}
          >
            <Select.Option value={MULANT}>{MULANT}</Select.Option>
            <Select.Option value={LEVEL_1}>{LEVEL_1}</Select.Option>
            <Select.Option value={LEVEL_2}>{LEVEL_2}</Select.Option>
            <Select.Option value={UCCHTAR}>{UCCHTAR}</Select.Option>
          </Select>
        </Form.Item>
      ),
    },
    {
      title: Text({
        tid: "SCHOOL_2026_PT2_Q6_C",
        def: "Examination Institution Name",
      }),
      dataIndex: "examinationInstitutionName",
      key: "examinationInstitutionName",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_2026_PT2_Q6", index, "examinationInstitutionName"]}
          rules={inputRequiredRule}
        >
          <Input
            disabled={disabled || isReviewMode}
            placeholder={Q6_C_PLACEHOLDER}
          />
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "SCHOOL_2026_PT2_Q6_D", def: "Year" }),
      dataIndex: "year",
      key: "year",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_2026_PT2_Q6", index, "year"]}
          rules={inputRequiredRule}
        >
          <DatePicker
            placeholder={Text({
              tid: "SELECT_YEAR",
              def: "Select Year",
            })}
            disabled={disabled || isReviewMode}
            disabledDate={disableFutureYears}
            picker="year"
            onKeyDown={(e) => e.preventDefault()}
            format="YYYY"
            onChange={(date, dateString) =>
              handleDateChange(
                form,
                "SCHOOL_2026_PT2_Q6",
                index,
                Array.isArray(dateString) ? dateString[0] : dateString,
                handleFieldChange,
              )
            }
          />
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "SCHOOL_2026_PT2_Q6_E", def: "Category" }),
      dataIndex: "category",
      key: "category",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_2026_PT2_Q6", index, "category"]}
          rules={inputRequiredRule}
        >
          <Input
            disabled={disabled || isReviewMode}
            placeholder={Q6_E_PLACEHOLDER}
          />
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "ACTION", def: "ACTION" }),
      dataIndex: "action",
      width: "100px",

      key: "action",
      render: (_, record, index) => (
        <Form.Item>
          <Button
            disabled={disabled || isReviewMode}
            variant="filled"
            onClick={() => removeRow(form, "SCHOOL_2026_PT2_Q6", index)}
            className="actionButton"
          >
            {Text({ tid: "REMOVE", def: "remove" })}
          </Button>
        </Form.Item>
      ),
    },
  ];

  // Q7 -----------------------------------------Q7-TABLE-COLUMNS-AND-DATA-SOURCE-----------------------------------------
  const SCHOOL_2026_PT2_Q7: TableProps<SCHOOL_2026_PT2_Q7>["columns"] = [
    {
      title: Text({ tid: "SERIAL_NUMBER", def: "SERIAL_NUMBER" }),
      dataIndex: "serialNumber",
      key: "serialNumber",
      width: "100px",
      render: (_, record, index) => getNumberInLanguage(index + 1),
    },
    {
      title: Text({ tid: "SCHOOL_2026_PT2_Q7_A", def: "Ved student's Name" }),
      dataIndex: "vedicStudentName",
      key: "vedicStudentName",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_2026_PT2_Q7", index, "vedicStudentName"]}
          rules={inputRequiredRule}
        >
          <Input
            disabled={disabled || isReviewMode}
            placeholder={Q7_A_PLACEHOLDER}
          />
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "SCHOOL_2026_PT2_Q7_B", def: "Award Name" }),
      dataIndex: "awardName",
      key: "awardName",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_2026_PT2_Q7", index, "awardName"]}
          rules={inputRequiredRule}
        >
          <Input
            disabled={disabled || isReviewMode}
            placeholder={Q7_B_PLACEHOLDER}
          />
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "SCHOOL_2026_PT2_Q7_C", def: "Institution Name" }),
      dataIndex: "institutionName",
      key: "institutionName",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_2026_PT2_Q7", index, "institutionName"]}
          rules={inputRequiredRule}
        >
          <Input
            disabled={disabled || isReviewMode}
            placeholder={Q7_C_PLACEHOLDER}
          />
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "SCHOOL_2026_PT2_Q7_D", def: "Year" }),
      dataIndex: "year",
      key: "year",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_2026_PT2_Q7", index, "year"]}
          rules={inputRequiredRule}
        >
          <DatePicker
            placeholder={Text({
              tid: "SELECT_YEAR",
              def: "Select Year",
            })}
            disabled={disabled || isReviewMode}
            disabledDate={disableFutureYears}
            picker="year"
            onKeyDown={(e) => e.preventDefault()}
            format="YYYY"
            onChange={(date, dateString) =>
              handleDateChange(
                form,
                "SCHOOL_2026_PT2_Q7",
                index,
                Array.isArray(dateString) ? dateString[0] : dateString,
                handleFieldChange,
              )
            }
          />
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "ACTION", def: "ACTION" }),
      dataIndex: "action",
      width: "100px",

      key: "action",
      render: (_, record, index) => (
        <Form.Item>
          <Button
            disabled={disabled || isReviewMode}
            variant="filled"
            onClick={() => removeRow(form, "SCHOOL_2026_PT2_Q7", index)}
            className="actionButton"
          >
            {Text({ tid: "REMOVE", def: "remove" })}
          </Button>
        </Form.Item>
      ),
    },
  ];

  // Q8 -----------------------------------------Q8-TABLE-COLUMNS-AND-DATA-SOURCE-----------------------------------------
  const SCHOOL_2026_PT2_Q8: TableProps<SCHOOL_2026_PT2_Q8>["columns"] = [
    {
      title: Text({ tid: "SERIAL_NUMBER", def: "SERIAL_NUMBER" }),
      dataIndex: "serialNumber",
      key: "serialNumber",
      width: "100px",
      render: (_, record, index) => getNumberInLanguage(index + 1),
    },
    {
      title: Text({
        tid: "SCHOOL_2026_PT2_Q8_A",
        def: "Former student's Name",
      }),
      dataIndex: "formarStudentName",
      key: "formarStudentName",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_2026_PT2_Q8", index, "formarStudentName"]}
          rules={inputRequiredRule}
        >
          <Input
            disabled={disabled || isReviewMode}
            placeholder={Q8_A_PLACEHOLDER}
          />
        </Form.Item>
      ),
    },
    {
      title: Text({
        tid: "SCHOOL_2026_PT2_Q8_B",
        def: "Year of Ved Study Completion",
      }),
      dataIndex: "year",
      key: "year",
      render: (_, record, index) => (
        <>
          {/* <Form.Item
            name={["SCHOOL_2026_PT2_Q8", index, "year"]}
            rules={inputRequiredRule}
          >
            <Input
              disabled={disabled || isReviewMode}
            />
          </Form.Item> */}
          <Form.Item
            name={["SCHOOL_2026_PT2_Q8", index, "year"]}
            rules={inputRequiredRule}
          >
            <DatePicker
              placeholder={Text({
                tid: "SELECT_YEAR",
                def: "Select Year",
              })}
              disabled={disabled || isReviewMode}
              disabledDate={disableFutureYears}
              picker="year"
              onKeyDown={(e) => e.preventDefault()}
              format="YYYY"
              onChange={(date, dateString) =>
                handleDateChange(
                  form,
                  "SCHOOL_2026_PT2_Q8",
                  index,
                  Array.isArray(dateString) ? dateString[0] : dateString,
                  handleFieldChange,
                )
              }
            />
          </Form.Item>
        </>
      ),
    },
    {
      title: Text({ tid: "SCHOOL_2026_PT2_Q8_C", def: "Ved Branch" }),
      dataIndex: "vedicBranch",
      key: "vedicBranch",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_2026_PT2_Q8", index, "vedicBranch"]}
          rules={inputRequiredRule}
        >
          <Select
            disabled={disabled || isReviewMode}
            placeholder={Text({
              tid: "SELECT_VEDA_SHAKHA",
              def: "Select Veda Shakha",
            })}
            showSearch
            optionFilterProp="children"
          >
            {Object.values(vedMapping).flatMap((veda) =>
              veda.vedashakhas.map((shakha, idx) => (
                <Select.Option key={`${shakha}-${idx}`} value={shakha}>
                  {shakha}
                </Select.Option>
              )),
            )}
          </Select>
        </Form.Item>
      ),
    },
    {
      title: Text({
        tid: "SCHOOL_2026_PT2_Q8_D",
        def: "Current Institution of Ved Teaching",
      }),
      dataIndex: "currentInstitutionOfVedicTeaching",
      key: "currentInstitutionOfVedicTeaching",
      render: (_, record, index) => (
        <Form.Item
          name={[
            "SCHOOL_2026_PT2_Q8",
            index,
            "currentInstitutionOfVedicTeaching",
          ]}
          rules={inputRequiredRule}
        >
          <Input
            disabled={disabled || isReviewMode}
            placeholder={Q8_D_PLACEHOLDER}
          />
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "ACTION", def: "ACTION" }),
      dataIndex: "action",
      key: "action",
      width: "100px",

      render: (_, record, index) => (
        <Form.Item>
          <Button
            disabled={disabled || isReviewMode}
            variant="filled"
            onClick={() => removeRow(form, "SCHOOL_2026_PT2_Q8", index)}
            className="actionButton"
          >
            {Text({ tid: "REMOVE", def: "remove" })}
          </Button>
        </Form.Item>
      ),
    },
  ];

  // Q9 -----------------------------------------Q9-TABLE-COLUMNS-AND-DATA-SOURCE-----------------------------------------
  const SCHOOL_2026_PT2_Q9: TableProps<SCHOOL_2026_PT2_Q9>["columns"] = [
    {
      title: Text({ tid: "SERIAL_NUMBER", def: "SERIAL_NUMBER" }),
      dataIndex: "serialNumber",
      key: "serialNumber",
      width: "100px",
      render: (_, record, index) => getNumberInLanguage(index + 1),
    },
    {
      title: Text({ tid: "SCHOOL_2026_PT2_Q9_A", def: "Award/Honor Name" }),
      dataIndex: "awardHonorName",
      key: "awardHonorName",
      render: (_, record, index) => {
        const docUrl = form.getFieldValue([
          "SCHOOL_2026_PT2_Q9",
          index,
          "SCHOOL_2026_PT2_Q9_DOC",
        ]);
        return (
          <Form.Item
            name={["SCHOOL_2026_PT2_Q9", index, "awardHonorName"]}
            rules={inputRequiredRule}
          >
            <Input
              disabled={disabled || !!docUrl || isReviewMode}
              placeholder={Q9_A_PLACEHOLDER}
            />
          </Form.Item>
        );
      },
    },
    {
      title: Text({ tid: "SCHOOL_2026_PT2_Q9_B", def: "Institution Name" }),
      dataIndex: "institutionName",
      key: "institutionName",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_2026_PT2_Q9", index, "institutionName"]}
          rules={inputRequiredRule}
        >
          <Input
            disabled={disabled || isReviewMode}
            placeholder={Q9_B_PLACEHOLDER}
          />
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "SCHOOL_2026_PT2_Q9_C", def: "Year" }),
      dataIndex: "year",
      key: "year",
      render: (_, record, index) => (
        <Form.Item
          name={["SCHOOL_2026_PT2_Q9", index, "year"]}
          rules={inputRequiredRule}
        >
          <DatePicker
            placeholder={Text({
              tid: "SELECT_YEAR",
              def: "Select Year",
            })}
            disabled={disabled || isReviewMode}
            disabledDate={disableFutureYears}
            picker="year"
            format="YYYY"
            onKeyDown={(e) => e.preventDefault()}
            onChange={(date, dateString) =>
              handleDateChange(
                form,
                "SCHOOL_2026_PT2_Q9",
                index,
                Array.isArray(dateString) ? dateString[0] : dateString,
                handleFieldChange,
              )
            }
          />
        </Form.Item>
      ),
    },
    {
      title: Text({ tid: "ATTACHED_DOCUMENT", def: "Document Upload" }),
      dataIndex: "documentUpload",
      key: "documentUpload",
      width: "20%",
      render: (_: any, record: any, index: number) => {
        const docUrl = form.getFieldValue([
          "SCHOOL_2026_PT2_Q9",
          index,
          "SCHOOL_2026_PT2_Q9_DOC",
        ]);
        const awardHonorName = form.getFieldValue([
          "SCHOOL_2026_PT2_Q9",
          index,
          "awardHonorName",
        ]);
        return (
          <DocumentUploadTableField
            formId={formId || ""}
            userId={userId || ""}
            labelName={`SCHOOL_2026_PT2_Q9_${index}`}
            existingDocumentUrl={docUrl}
            disabled={disabled || !awardHonorName || isReviewMode}
            onUploadComplete={(docUrl) => {
              form.setFieldValue(
                ["SCHOOL_2026_PT2_Q9", index, "SCHOOL_2026_PT2_Q9_DOC"],
                docUrl,
              );
              handleFieldChange(
                "SCHOOL_2026_PT2_Q9",
                JSON.stringify(form.getFieldValue("SCHOOL_2026_PT2_Q9")),
              );
              // Update Q16 summary table
              setTimeout(async () => {
                const uploadedDocuments = getUploadedDocuments();
                form.setFieldsValue({
                  SCHOOL_2026_PT2_Q10: uploadedDocuments,
                });
                await handleFieldChange(
                  "SCHOOL_2026_PT2_Q10",
                  JSON.stringify(uploadedDocuments),
                );
              }, 100);
            }}
          />
        );
      },
    },
    {
      title: Text({ tid: "ACTION", def: "ACTION" }),
      dataIndex: "action",
      width: "100px",

      key: "action",
      render: (_, record, index) => (
        <Form.Item>
          <Button
            disabled={disabled || isReviewMode}
            variant="filled"
            onClick={() => removeRow(form, "SCHOOL_2026_PT2_Q9", index)}
            className="actionButton"
          >
            {Text({ tid: "REMOVE", def: "remove" })}
          </Button>
        </Form.Item>
      ),
    },
  ];

  // Q10 -----------------------------------------Q10-TABLE-COLUMNS-AND-DATA-SOURCE-----------------------------------------

  const TABLE_COLUMNS_SCHOOL_2026_PT2_Q10: TableProps<DATA_TYPE_SCHOOL_2026_PT2_Q10>["columns"] =
    [
      {
        title: Text({ tid: "SERIAL_NUMBER", def: "SERIAL_NUMBER" }),
        dataIndex: "serialNumber",
        key: "serialNumber",
        width: "100px",
        render: (_, record, index) => getNumberInLanguage(index + 1),
      },
      {
        title: Text({ tid: "DOCUMENT_DETAILS", def: "Document Details" }),
        dataIndex: "documentDetails",
        key: "documentDetails",
      },
      {
        title: Text({ tid: "PREVIEW", def: "Preview" }),
        dataIndex: "documentUrl",
        key: "documentUrl",
        render: (_, record: any) => {
          const documentUrl = record.documentUrl;
          if (!documentUrl)
            return <span style={{ color: "#ccc" }}>No document</span>;

          const fullUrl = `${process.env.REACT_APP_PUBLIC_API_MEDIA_BASEURL}${documentUrl}`;
          const isPdf = documentUrl.toLowerCase().endsWith(".pdf");

          if (isPdf) {
            return (
              <a
                href={fullUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="primary-link"
              >
                <FilePdfOutlined
                  style={{ fontSize: "24px", color: "#ff4d4f" }}
                />
              </a>
            );
          }

          return <Image width={50} src={fullUrl} alt="Preview" />;
        },
      },
    ];

  const inputRequiredBasicRule: Rule[] = [
    {
      required: true,
      message: Text({ tid: "REQUIRED", def: "Required" }),
    },
  ];

  return (
    <div
      id="schoolFormPart2"
      className={`${styles.formWrapper} studentTeacherSchoolFormWrapper`}
    >
      <Form
        {...formItemLayout}
        form={form}
        name="register"
        onFinish={handleFormSubmit}
        onValuesChange={handleLocalFormChange}
        scrollToFirstError
        layout="vertical"
      >
        {/* PAGE HEADING */}
        <Row>
          <Col>
            <Typography.Title level={3}>
              {Text({
                tid: "SCHOOL_2026_PART_2_FORM_HEADING",
                def: "वेदविद्यालय आवेदनपत्र - भाग - २",
              })}
            </Typography.Title>
            {!disabled && (
              <Typography.Text type="danger">
                {Text({
                  tid: "all_fields_are_mandatory",
                  def: "All fields are mandatory!",
                })}
              </Typography.Text>
            )}
          </Col>
        </Row>
        {isReviewMode && (
          <Row style={{ marginBottom: 10 }}>
            <Typography.Text type="warning">
              <CheckCircleOutlined style={{ marginRight: 6 }} />
              {Text({
                tid: "REVIEW_MODE_INSTRUCTION",
                def: "You are in review mode. You can only view the submitted data. To make changes, please switch to edit mode.",
              })}
            </Typography.Text>
          </Row>
        )}

        {/* Q1). HEADING SCHOOL_2026_PT2_Q1 */}

        {/* 1. Name of Vedvidhyalaya and full address */}
        <Row>
          <Col xs={24} lg={12} xl={8}>
            <Form.Item
              name="SCHOOL_2026_PT2_Q1"
              label={Text({
                tid: "SCHOOL_2026_PT2_Q1",
                def: "Name of Vedvidhyalaya",
              })}
              rules={inputRequiredBasicRule}
            >
              <Input disabled={true} placeholder={Q1_PLACEHOLDER} />
            </Form.Item>
          </Col>
        </Row>

        {/* Q2). HEADING SCHOOL_2026_PT2_Q2 */}
        <Row>
          <Col xs={24}>
            <Typography.Text>
              {Text({
                tid: "SCHOOL_2026_PT2_Q2",
                def: "2. Details of teachers currently working in the institution:",
              })}
              <FormHelpTooltip
                title={Text({
                  tid: "SCHOOL_2026_PT2_Q2_TOOLTIP",
                  def: "List all teachers currently working in the Ved school.",
                })}
              />
            </Typography.Text>
          </Col>
        </Row>
        {/* Q2). TABLE SCHOOL_2026_PT2_Q2 DUE */}
        <Row>
          <Col xs={24}>
            <Form.Item name="SCHOOL_2026_PT2_Q2">
              <Table
                columns={SCHOOL_2026_PT2_Q2}
                dataSource={
                  Array.isArray(form.getFieldValue("SCHOOL_2026_PT2_Q2"))
                    ? form.getFieldValue("SCHOOL_2026_PT2_Q2")
                    : []
                }
                pagination={false}
                style={{ width: "100%" }}
                rowKey={(record, index) =>
                  index !== undefined ? index.toString() : ""
                }
              />
            </Form.Item>
            {!disabled && (
              <Flex className="belowTableAddRemoveButton">
                <Button
                  disabled={disabled || isReviewMode}
                  onClick={() => {
                    const currentData =
                      form.getFieldValue("SCHOOL_2026_PT2_Q2") || [];
                    const newRow = {
                      teacherName: "",
                      vedaShakha: null,
                      vedicStudyLevel: null,
                      age: "",
                      fullTimeOrPartTime: null,
                      SCHOOL_2026_PT2_Q2_DOC: "",
                    };
                    form.setFieldsValue({
                      SCHOOL_2026_PT2_Q2: [...currentData, newRow],
                    });
                    setRowCount((prev) => ({
                      ...prev,
                      SCHOOL_2026_PT2_Q2: prev.SCHOOL_2026_PT2_Q2 + 1,
                    }));
                  }}
                >
                  {Text({ tid: "ADD", def: "Add" })}
                </Button>
              </Flex>
            )}
          </Col>
        </Row>

        {/* Q3). HEADING SCHOOL_2026_PT2_Q3 */}
        <Row>
          <Col xs={24}>
            <Typography.Text>
              {Text({
                tid: "SCHOOL_2026_PT2_Q3",
                def: "3. Details of previously worked teachers (who left in the last 3 years):",
              })}
              <FormHelpTooltip
                title={Text({
                  tid: "SCHOOL_2026_PT2_Q3_TOOLTIP",
                  def: "Details of teachers who left the institution in the last 3 years.",
                })}
              />
            </Typography.Text>
          </Col>
        </Row>
        {/* Q3). TABLE SCHOOL_2026_PT2_Q3 DUE */}
        <Row>
          <Col xs={24}>
            <Form.Item name="SCHOOL_2026_PT2_Q3">
              <Table
                columns={SCHOOL_2026_PT2_Q3}
                dataSource={form.getFieldValue("SCHOOL_2026_PT2_Q3") || []}
                pagination={false}
                style={{ width: "100%" }}
                rowKey={(record, index) =>
                  index !== undefined ? index.toString() : ""
                }
              />
            </Form.Item>
            {!disabled && (
              <Flex className="belowTableAddRemoveButton">
                <Button
                  disabled={disabled || isReviewMode}
                  onClick={() => {
                    const currentData =
                      form.getFieldValue("SCHOOL_2026_PT2_Q3") || [];
                    const newRow = {
                      teacherName: "",
                      vedaShakha: null,
                      startingOfTeaching: "",
                      endingOfTeaching: "",
                      reasonOfLeaving: "",
                    };
                    form.setFieldsValue({
                      SCHOOL_2026_PT2_Q3: [...currentData, newRow],
                    });
                    setRowCount((prev) => ({
                      ...prev,
                      SCHOOL_2026_PT2_Q3: prev.SCHOOL_2026_PT2_Q3 + 1,
                    }));
                  }}
                >
                  {Text({ tid: "ADD", def: "Add" })}
                </Button>
              </Flex>
            )}
          </Col>
        </Row>

        {/* Q4). HEADING SCHOOL_2026_PT2_Q4 */}
        <Row>
          <Col xs={24}>
            <Typography.Text>
              {Text({
                tid: "SCHOOL_2026_PT2_Q4",
                def: "4. Details of awards received by teachers:",
              })}
              <FormHelpTooltip
                title={Text({
                  tid: "SCHOOL_2026_PT2_Q4_TOOLTIP",
                  def: "Awards received by teachers for their contribution to Vedic education.",
                })}
              />
            </Typography.Text>
          </Col>
        </Row>
        {/* Q4). TABLE SCHOOL_2026_PT2_Q4 DUE */}
        <Row>
          <Col xs={24}>
            <Form.Item name="SCHOOL_2026_PT2_Q4">
              <Table
                columns={SCHOOL_2026_PT2_Q4}
                dataSource={form.getFieldValue("SCHOOL_2026_PT2_Q4") || []}
                pagination={false}
                style={{ width: "100%" }}
                rowKey={(record, index) =>
                  index !== undefined ? index.toString() : ""
                }
              />
            </Form.Item>
            {!disabled && (
              <Flex className="belowTableAddRemoveButton">
                <Button
                  disabled={disabled || isReviewMode}
                  onClick={() => {
                    const currentData =
                      form.getFieldValue("SCHOOL_2026_PT2_Q4") || [];
                    const newRow = {
                      teacherName: "",
                      awardName: "",
                      instituteName: "",
                      year: "",
                    };
                    form.setFieldsValue({
                      SCHOOL_2026_PT2_Q4: [...currentData, newRow],
                    });
                    setRowCount((prev) => ({
                      ...prev,
                      SCHOOL_2026_PT2_Q4: prev.SCHOOL_2026_PT2_Q4 + 1,
                    }));
                  }}
                >
                  {Text({ tid: "ADD", def: "Add" })}
                </Button>
              </Flex>
            )}
          </Col>
        </Row>

        {/* Q5). HEADING SCHOOL_2026_PT2_Q5 */}
        <Row>
          <Col xs={24}>
            <Typography.Text>
              {Text({
                tid: "SCHOOL_2026_PT2_Q5",
                def: "5. Details of students currently studying in the institution:",
              })}
              <FormHelpTooltip
                title={Text({
                  tid: "SCHOOL_2026_PT2_Q5_TOOLTIP",
                  def: "Details of students currently studying in the Ved school.",
                })}
              />
            </Typography.Text>
          </Col>
        </Row>
        {/* Q5). TABLE SCHOOL_2026_PT2_Q5 DUE */}
        <Row>
          <Col xs={24}>
            <Form.Item name="SCHOOL_2026_PT2_Q5">
              <Table
                columns={SCHOOL_2026_PT2_Q5}
                // dataSource={form.getFieldValue("SCHOOL_2026_PT2_Q5") || []}
                dataSource={
                  Array.isArray(form.getFieldValue("SCHOOL_2026_PT2_Q5"))
                    ? form.getFieldValue("SCHOOL_2026_PT2_Q5")
                    : []
                }
                pagination={false}
                style={{ width: "100%" }}
                rowKey={(record, index) =>
                  index !== undefined ? index.toString() : ""
                }
              />
            </Form.Item>
            {!disabled && (
              <Flex className="belowTableAddRemoveButton">
                <Button
                  disabled={disabled || isReviewMode}
                  onClick={() => {
                    const currentData =
                      form.getFieldValue("SCHOOL_2026_PT2_Q5") || [];
                    const newRow = {
                      vedicStudentName: "",
                      age: "",
                      vedaBranch: "",
                      studyLevel: "",
                    };
                    form.setFieldsValue({
                      SCHOOL_2026_PT2_Q5: [...currentData, newRow],
                    });
                    setRowCount((prev) => ({
                      ...prev,
                      SCHOOL_2026_PT2_Q5: prev.SCHOOL_2026_PT2_Q5 + 1,
                    }));
                  }}
                >
                  {Text({ tid: "ADD", def: "Add" })}
                </Button>
              </Flex>
            )}
          </Col>
        </Row>

        {/* Q6). HEADING SCHOOL_2026_PT2_Q6 */}
        <Row>
          <Col xs={24}>
            <Typography.Text>
              {Text({
                tid: "SCHOOL_2026_PT2_Q6",
                def: "6. Details of students from the institution who appeared for Vedic exams in other institutions:",
              })}
              <FormHelpTooltip
                title={Text({
                  tid: "SCHOOL_2026_PT2_Q6_TOOLTIP",
                  def: "Details of students who appeared for Vedic exams in other institutions.",
                })}
              />
            </Typography.Text>
          </Col>
        </Row>
        {/* Q6). TABLE SCHOOL_2026_PT2_Q6 DUE */}
        <Row>
          <Col xs={24}>
            <Form.Item name="SCHOOL_2026_PT2_Q6">
              <Table
                columns={SCHOOL_2026_PT2_Q6}
                dataSource={form.getFieldValue("SCHOOL_2026_PT2_Q6") || []}
                pagination={false}
                style={{ width: "100%" }}
                rowKey={(record, index) =>
                  index !== undefined ? index.toString() : ""
                }
              />
            </Form.Item>
            {!disabled && (
              <Flex className="belowTableAddRemoveButton">
                <Button
                  disabled={disabled || isReviewMode}
                  onClick={() => {
                    const currentData =
                      form.getFieldValue("SCHOOL_2026_PT2_Q6") || [];
                    const newRow = {
                      vedicStudentName: "",
                      vedicStudyLevel: null,
                      examinationInstitutionName: "",
                      year: "",
                      category: "",
                    };
                    form.setFieldsValue({
                      SCHOOL_2026_PT2_Q6: [...currentData, newRow],
                    });
                    setRowCount((prev) => ({
                      ...prev,
                      SCHOOL_2026_PT2_Q6: prev.SCHOOL_2026_PT2_Q6 + 1,
                    }));
                  }}
                >
                  {Text({ tid: "ADD", def: "Add" })}
                </Button>
              </Flex>
            )}
          </Col>
        </Row>

        {/* Q7). HEADING SCHOOL_2026_PT2_Q7 */}
        <Row>
          <Col xs={24}>
            <Typography.Text>
              {Text({
                tid: "SCHOOL_2026_PT2_Q7",
                def: "7. Details of awards/honors received by students:",
              })}
              <FormHelpTooltip
                title={Text({
                  tid: "SCHOOL_2026_PT2_Q7_TOOLTIP",
                  def: "Awards received by students in Vedic competitions or exams.",
                })}
              />
            </Typography.Text>
          </Col>
        </Row>
        {/* Q7). TABLE SCHOOL_2026_PT2_Q7 DUE */}
        <Row>
          <Col xs={24}>
            <Form.Item name="SCHOOL_2026_PT2_Q7">
              <Table
                columns={SCHOOL_2026_PT2_Q7}
                dataSource={form.getFieldValue("SCHOOL_2026_PT2_Q7") || []}
                pagination={false}
                style={{ width: "100%" }}
                rowKey={(record, index) =>
                  index !== undefined ? index.toString() : ""
                }
              />
            </Form.Item>
            {!disabled && (
              <Flex className="belowTableAddRemoveButton">
                <Button
                  disabled={disabled || isReviewMode}
                  onClick={() => {
                    const currentData =
                      form.getFieldValue("SCHOOL_2026_PT2_Q7") || [];
                    const newRow = {
                      vedicStudentName: "",
                      awardName: "",
                      institutionName: "",
                      year: "",
                    };
                    form.setFieldsValue({
                      SCHOOL_2026_PT2_Q7: [...currentData, newRow],
                    });
                    setRowCount((prev) => ({
                      ...prev,
                      SCHOOL_2026_PT2_Q7: prev.SCHOOL_2026_PT2_Q7 + 1,
                    }));
                  }}
                >
                  {Text({ tid: "ADD", def: "Add" })}
                </Button>
              </Flex>
            )}
          </Col>
        </Row>

        {/* Q8). HEADING SCHOOL_2026_PT2_Q8 */}
        <Row>
          <Col xs={24}>
            <Typography.Text>
              {Text({
                tid: "SCHOOL_2026_PT2_Q8",
                def: "8. Details of former students of the institution who are currently providing Vedic education in other institutions:",
              })}
              <FormHelpTooltip
                title={Text({
                  tid: "SCHOOL_2026_PT2_Q8_TOOLTIP",
                  def: "Details of former students of the institution who are currently providing Vedic education in other institutions.",
                })}
              />
            </Typography.Text>
          </Col>
        </Row>
        {/* Q8). TABLE SCHOOL_2026_PT2_Q8 DUE */}
        <Row>
          <Col xs={24}>
            <Form.Item name="SCHOOL_2026_PT2_Q8">
              <Table
                columns={SCHOOL_2026_PT2_Q8}
                // dataSource={form.getFieldValue("SCHOOL_2026_PT2_Q8") || []}
                dataSource={
                  Array.isArray(form.getFieldValue("SCHOOL_2026_PT2_Q8"))
                    ? form.getFieldValue("SCHOOL_2026_PT2_Q8")
                    : []
                }
                pagination={false}
                style={{ width: "100%" }}
                rowKey={(record, index) =>
                  index !== undefined ? index.toString() : ""
                }
              />
            </Form.Item>
            {!disabled && (
              <Flex className="belowTableAddRemoveButton">
                <Button
                  disabled={disabled || isReviewMode}
                  onClick={() => {
                    const currentData =
                      form.getFieldValue("SCHOOL_2026_PT2_Q8") || [];
                    const newRow = {
                      formarStudentName: "",
                      year: "",
                      vedicBranch: null,
                      currentInstitutionOfVedicTeaching: "",
                    };
                    form.setFieldsValue({
                      SCHOOL_2026_PT2_Q8: [...currentData, newRow],
                    });
                    setRowCount((prev) => ({
                      ...prev,
                      SCHOOL_2026_PT2_Q8: prev.SCHOOL_2026_PT2_Q8 + 1,
                    }));
                  }}
                >
                  {Text({ tid: "ADD", def: "Add" })}
                </Button>
              </Flex>
            )}
          </Col>
        </Row>

        {/* Q9). HEADING SCHOOL_2026_PT2_Q9 */}
        <Row>
          <Col xs={24}>
            <Typography.Text>
              {Text({
                tid: "SCHOOL_2026_PT2_Q9",
                def: "9. Awards/Honors received by the institution:",
              })}
              {/* <FormHelpTooltip
                title={Text({
                  tid: "SCHOOL_2026_PT2_Q9_TOOLTIP",
                  def: "Awards/Honors received by the institution.",
                })}
              /> */}
            </Typography.Text>
          </Col>
        </Row>
        {/* Q9). TABLE SCHOOL_2026_PT2_Q9 DUE */}
        <Row className="mb-20">
          <Col xs={24}>
            <Form.Item name="SCHOOL_2026_PT2_Q9">
              <Table
                columns={SCHOOL_2026_PT2_Q9}
                dataSource={form.getFieldValue("SCHOOL_2026_PT2_Q9") || []}
                pagination={false}
                style={{ width: "100%" }}
                rowKey={(record, index) =>
                  index !== undefined ? index.toString() : ""
                }
              />
            </Form.Item>
            {!disabled && (
              <Flex className="belowTableAddRemoveButton">
                <Button
                  disabled={disabled || isReviewMode}
                  onClick={() => {
                    const currentData =
                      form.getFieldValue("SCHOOL_2026_PT2_Q9") || [];
                    const newRow = {
                      awardHonorName: "",
                      institutionName: "",
                      year: "",
                      SCHOOL_2026_PT2_Q9_DOC: "",
                    };
                    form.setFieldsValue({
                      SCHOOL_2026_PT2_Q9: [...currentData, newRow],
                    });
                    setRowCount((prev) => ({
                      ...prev,
                      SCHOOL_2026_PT2_Q9: prev.SCHOOL_2026_PT2_Q9 + 1,
                    }));
                  }}
                >
                  {Text({ tid: "ADD", def: "Add" })}
                </Button>
              </Flex>
            )}
          </Col>
        </Row>

        {/* Q10). AUTO-FILLED UPLOADED DOCUMENTS TABLE */}
        <Row>
          <Col xs={24}>
            <Typography.Text>
              {Text({
                tid: "SCHOOL_2026_PT2_Q16",
                def: "Details of documents attached with this application:",
              })}
              <FormHelpTooltip
                title={Text({
                  tid: "SCHOOL_2026_PT2_Q16_TOOLTIP",
                  def: "Summary of all documents uploaded in this form.",
                })}
              />
            </Typography.Text>

            <Typography.Text>
              {Text({
                tid: "SCHOOL_2026_PT2_Q16_A",
                def: "Details of documents attached with this application:",
              })}
            </Typography.Text>
          </Col>
        </Row>
        <Row className="mb-20">
          <Col xs={24}>
            <Form.Item name="SCHOOL_2026_PT2_Q10">
              <Table
                columns={TABLE_COLUMNS_SCHOOL_2026_PT2_Q10}
                // dataSource={form.getFieldValue("SCHOOL_2026_PT2_Q10") || []}
                dataSource={
                  Array.isArray(form.getFieldValue("SCHOOL_2026_PT2_Q16"))
                    ? form.getFieldValue("SCHOOL_2026_PT2_Q16")
                    : []
                }
                pagination={false}
                style={{ width: "100%" }}
                rowKey={(record, index) =>
                  index !== undefined ? index.toString() : ""
                }
              />
            </Form.Item>
          </Col>
        </Row>

        {/* INSTITUTION HEAD AFFIDAVIT TITLE */}
        <div id="school_form2_ceo_affidavit" className="affidavitSection">
          <Row>
            <Col>
              <Typography.Text>
                {Text({
                  tid: "INSTITUTION_HEAD_AFFIDAVIT_TITLE",
                  def: "Affidavit by the Head of the Institution",
                })}
                <FormHelpTooltip
                  title={Text({
                    tid: "SCHOOL_2026_PT2_Q11_TOOLTIP",
                    def: "Please read the affidavit carefully before signing.",
                  })}
                />
              </Typography.Text>
            </Col>
          </Row>
          {/* INSTITUTION HEAD AFFIDAVIT INTRO */}
          <Row className="affidavitRow">
            <Col xs={24}>
              <Typography.Text
                type="secondary"
                style={{
                  maxWidth: "max-content",
                  marginBottom: "0 !important",
                }}
              >
                {Text({
                  tid: "INSTITUTION_HEAD_AFFIDAVIT_INTRO_1",
                  def: "मैं (संस्था प्रमुख का पूरा नाम)",
                })}
              </Typography.Text>
              <Form.Item
                name="SCHOOL_2026_PT2_Q11"
                rules={[
                  {
                    required: true,
                    message: Text({ tid: "REQUIRED", def: "Required" }),
                  },
                ]}
                className="affidavitRowInput"
              >
                <Input disabled={disabled || isReviewMode} />
              </Form.Item>
              <Typography.Text
                type="secondary"
                style={{ maxWidth: "max-content", marginBottom: "0" }}
              >
                {Text({
                  tid: "INSTITUTION_HEAD_AFFIDAVIT_INTRO_2",
                  def: "एतद् द्वारा सत्यनिष्ठा से यह प्रमाणित करता हूँ कि :",
                })}
              </Typography.Text>
            </Col>
          </Row>

          {/* INSTITUTION HEAD AFFIDAVIT DESCRIPTION */}
          <ul>
            <li>
              <Row className="affidavitRow">
                <Col xs={24}>
                  <Typography.Text
                    type="secondary"
                    style={{ maxWidth: "max-content", marginBottom: "0" }}
                  >
                    {Text({
                      tid: "INSTITUTION_HEAD_AFFIDAVIT_CLAUSE_11",
                      def: "इस वेद विद्यालय में वेदाध्यापन विगत",
                    })}
                  </Typography.Text>
                  <Form.Item
                    name="SCHOOL_2026_PT2_Q12"
                    rules={numberInputRules({
                      required: true,
                      allowNegative: false,
                      min: 0,
                      max: 120,
                    })}
                    className="affidavitRowInput"
                  >
                    <Input
                      disabled={disabled || isReviewMode}
                      id={applyPdfStyles ? "affidavit_input" : ""}
                    />
                  </Form.Item>
                  <Typography.Text
                    type="secondary"
                    style={{ maxWidth: "max-content", marginBottom: "0" }}
                  >
                    {Text({
                      tid: "INSTITUTION_HEAD_AFFIDAVIT_CLAUSE_12",
                      def: "वर्षों से निरन्तर चल रहा है;",
                    })}
                  </Typography.Text>
                </Col>
              </Row>
            </li>
            <Row>
              <Col>
                <li>
                  <Typography.Text type="secondary">
                    {Text({
                      tid: "INSTITUTION_HEAD_AFFIDAVIT_CLAUSE_2",
                      def: "इस आवेदन पत्र में दी गई सभी जानकारी पूर्णतया सत्य है;",
                    })}
                  </Typography.Text>
                </li>
                <li>
                  <Typography.Text type="secondary">
                    {Text({
                      tid: "INSTITUTION_HEAD_AFFIDAVIT_CLAUSE_3",
                      def: "इस आवेदन से संलग्न सभी अनुबन्धों व प्रमाण-पत्रों में दी गई जानकारी सत्य है।",
                    })}
                  </Typography.Text>
                </li>
              </Col>
            </Row>
          </ul>
          <Row>
            <Col>
              <div className="mb-20">
                <Typography.Text type="secondary">
                  {Text({
                    tid: "INSTITUTION_HEAD_AFFIDAVIT_VERIFICATION",
                    def: "मैंने सभी तथ्यों की पुष्टि करने के बाद इस प्रमाण-पत्र पर हस्ताक्षर किये हैं। मैं इस शपथपत्र पर हस्ताक्षर करने के महत्व व निहितार्थ को पूरी तरह से समझता हूँ।",
                  })}
                </Typography.Text>

                {!disabled ? (
                  <Form.Item
                    name="affidavitAccepted"
                    valuePropName="checked"
                    rules={[
                      {
                        required: true,
                        transform: (value: boolean) => value || undefined,
                        type: "boolean",
                        message: Text({
                          tid: "AFFIDAVIT_ACCEPTANCE_REQUIRED",
                          def: "You must accept the affidavit declaration to submit the form",
                        }),
                      },
                    ]}
                    style={{ marginBottom: 0, marginTop: 16 }}
                  >
                    <Checkbox
                      checked={affidavitAccepted}
                      disabled={disabled || isReviewMode}
                    >
                      <Typography.Text type="secondary">
                        {Text({
                          tid: "INSTITUTION_HEAD_AFFIDAVIT_ACCEPTANCE",
                          def: "मैं स्वीकार करता हूँ कि यदि इस शपथ-पत्र में दी गई कोई भी जानकारी असत्य पाई गई तो यह वेदविद्यालय, इसमें कार्यरत सभी अध्यापक व अध्ययन कर रहे सभी विद्यार्थी हमेशा के लिये भारतात्मा पुरस्कार के लिये अयोग्य माने जा सकते हैं।",
                        })}
                      </Typography.Text>
                    </Checkbox>
                  </Form.Item>
                ) : (
                  <div style={{ marginTop: 16 }}>
                    <Form.Item
                      name="affidavitAccepted"
                      valuePropName="checked"
                      style={{ marginBottom: 0 }}
                    >
                      <Checkbox disabled={true} checked={affidavitAccepted}>
                        {Text({
                          tid: "AFFIDAVIT_ACCEPTANCE",
                          def: "I accept the above affidavit declaration",
                        })}
                      </Checkbox>
                    </Form.Item>
                  </div>
                )}
              </div>
            </Col>
          </Row>
          <div className="affidavitSection">
            {/* INSTITUTION HEAD SIGNATURE */}
            <Row className="QuestionsSubSection">
              <Col xs={24} lg={12} xl={8}>
                <DocumentUploadField
                  fieldName="SCHOOL_2026_PT2_Q13"
                  label={Text({
                    tid: "INSTITUTION_HEAD_SIGNATURE",
                    def: "Institution Head Signature",
                  })}
                  formId={formId || ""}
                  userId={userId || ""}
                  currentStage={eFORMSTATUS.STAGE2INPROGRESS}
                  disabled={disabled || isReviewMode}
                  existingDocuments={documentsData}
                  onUploadComplete={(docId) => {
                    handleFieldChange(
                      "SCHOOL_2026_PT2_Q13",
                      docId,
                      eAnswerType.FILE,
                    );
                  }}
                />
              </Col>
            </Row>
          </div>

          {/* NAME */}
          <Row className="QuestionsSubSection affidavitQuestionFirst">
            <Col xs={24} lg={12} xl={8}>
              <Form.Item
                name="SCHOOL_2026_PT2_Q14"
                label={Text({
                  tid: "INSTITUTION_HEAD_FULL_NAME",
                  def: "INSTITUTION_HEAD_FULL_NAME",
                })}
              >
                <Input
                  disabled={disabled || isReviewMode}
                  id={applyPdfStyles ? "affidavit_input" : ""}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* PLACE */}
          <Row className="QuestionsSubSection">
            <Col xs={24} lg={12} xl={8}>
              <Form.Item
                name="SCHOOL_2026_PT2_Q15"
                label={Text({ tid: "PLACE", def: "Place" })}
                rules={inputRequiredBasicRule}
              >
                <Input
                  disabled={disabled || isReviewMode}
                  maxLength={INPUT_LENGTH.BASIC_LENGTH}
                  placeholder={Text({ tid: "PLACE", def: "Place" })}
                />
              </Form.Item>
            </Col>
          </Row>
          {/* DATE */}
          <Row className="QuestionsSubSection">
            <Col xs={24} lg={12} xl={8}>
              <Form.Item
                name="SCHOOL_2026_PT2_Q16"
                label={Text({ tid: "DATE", def: "Date" })}
                rules={inputRequiredBasicRule}
                initialValue={dayjs()}
                getValueProps={(value) => ({
                  value:
                    value && dayjs(value).isValid() ? dayjs(value) : undefined,
                })}
              >
                <DatePicker
                  disabled={disabled || isReviewMode}
                  style={{ width: "100%" }}
                  format="DD-MM-YYYY"
                  placeholder="DD-MM-YYYY"
                  defaultValue={dayjs()}
                  onChange={(date, dateString) => {
                    // We store the ISO string or whatever format backend expects
                    // but usually DatePicker returns a Dayjs object in onChange's first arg
                    // Here we can rely on standard form behavior or force string
                    if (date) {
                      handleFieldChange(
                        "SCHOOL_2026_PT2_Q16",
                        date,
                        eAnswerType.DATE,
                      );
                    }
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
        </div>

        {/* -------------------------------------------------------------- */}
        {/* SUBMIT BUTTON */}
        <Row
          style={
            disabled === true
              ? { marginTop: "var(--bs-item-negative-margin-30)" }
              : undefined
          }
        >
          {disabled === false && !isReviewMode && (
            <Button
              block
              type="primary"
              onClick={handleNextClick}
              disabled={isLoading || !affidavitAccepted}
              className="formSubmitButton"
            >
              {Text({ tid: "NEXT", def: "Next" })}
            </Button>
          )}
          {disabled === false && isReviewMode && (
            <div style={{ display: "flex", gap: "16px", width: "100%" }}>
              <Button
                block
                onClick={handleBackClick}
                disabled={isLoading}
                className="formSubmitButton"
              >
                {Text({ tid: "BACK", def: "Back" })}
              </Button>
              <Button
                block
                type="primary"
                htmlType="submit"
                disabled={isLoading || !affidavitAccepted}
                className="formSubmitButton"
                loading={isSubmitting}
              >
                {Text({ tid: "SUBMIT", def: "Submit" })}
              </Button>
            </div>
          )}
        </Row>
      </Form>
    </div>
  );
};

export default SchoolPartTwo;
